import React, { useCallback, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Like from '../../ui/Like';
import AddToClosetButton from '../../ui/AddToClosetButton';
import { formatCurrency } from '../../../utils/formatters';
import { limitRentalOptions, formatRentalPeriod as formatRentalPeriodUtil } from '../../../utils/rentalPriceUtils';
import { useIncrementProductViewMutation } from '../../../redux/apis/endpoints/listingApi';

/** Format price without unnecessary decimals: $300 not $300.00 */
const formatPrice = (amount, currency) => {
  const formatted = formatCurrency(amount, currency);
  return formatted.replace(/\.00$/, '');
};

/**
 * Scrollable price bar — always scrollable when 3+ blocks
 */
const PriceBar = ({ product }) => {
  const scrollRef = useRef(null);
  const productCurrency = product.currency || 'USD';
  const allPrices = [];

  if (product.forSell && product.price) {
    allPrices.push({ label: 'Buy', price: product.price, isBuy: true });
  }

  if (product.forRent && product.rentPriceAndTime) {
    const limited = limitRentalOptions(product.rentPriceAndTime, 4);
    limited.forEach(option => {
      allPrices.push({
        label: formatRentalPeriodUtil(option),
        price: option.price,
        isBuy: false,
        currency: option.currency,
      });
    });
  }

  if (product.licensingEnabled) {
    if (product.licensingType === 'paid' && product.licensingPrice != null) {
      allPrices.push({
        label: 'License',
        price: product.licensingPrice,
        isBuy: true,
        currency: product.currency,
      });
    }
  }

  if (allPrices.length === 0) return null;

  const needsScroll = allPrices.length > 2;

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex rounded-md overflow-auto scrollbar-hide"
        style={{
          border: '1px solid rgba(26,26,26,0.08)',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {allPrices.map((p, i) => (
          <div
            key={i}
            className="text-center flex-shrink-0"
            style={{
              minWidth: 64,
              flex: 1,
              padding: '7px 8px',
              background: p.isBuy ? '#FAF7F2' : '#FFFFFF',
              borderRight: i < allPrices.length - 1 ? '1px solid rgba(26,26,26,0.08)' : 'none',
            }}
          >
            <div style={{
              fontFamily: "'DM Sans'", fontSize: 9, fontWeight: 400,
              color: '#B5AFA8', textTransform: 'uppercase',
              letterSpacing: 0.8, marginBottom: 1,
            }}>{p.label}</div>
            <div style={{
              fontFamily: "'DM Sans'", fontSize: 14, fontWeight: 600,
              color: p.isBuy ? '#1A1A1A' : '#1ABCB0',
            }}>{p.isTextPrice ? p.price : formatPrice(p.price, p.currency || productCurrency)}</div>
          </div>
        ))}
      </div>

      {needsScroll && (
        <div
          className="absolute top-0 right-0 bottom-0 flex items-center justify-center pointer-events-none"
          style={{
            width: 28,
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9))',
            borderRadius: '0 6px 6px 0',
          }}
        >
          <span style={{ fontSize: 10, color: '#B5AFA8' }}>›</span>
        </div>
      )}
    </div>
  );
};

const BrowseCard = ({ product }) => {
  const history = useHistory();
  const [isHovered, setIsHovered] = useState(false);

  const [incrementView] = useIncrementProductViewMutation();

  const handleCardClick = useCallback((product) => {
    // Fire and forget — don't await
    incrementView(product.id);
    if (product.activeListing === undefined) {
      history.push('/listing/edit/' + product.id);
    } else {
      history.push('/product/' + product.id);
    }
  }, [history, incrementView]);

  const handleInteractiveClick = (e) => {
    e.stopPropagation();
  };

  if (!product) return null;
  if (product.activeListing === false) return null;
  if (product.stock === '0' || product.stock === 0) return null;

  const hasPricing = product.forSell || product.forRent || (product.licensingEnabled && product.licensingType === 'paid' && product.licensingPrice != null);

  const getDesignerName = () => {
    const user = product.users_permissions_user;
    if (!user) return null;
    if (user.firstname || user.lastname) {
      return `${user.firstname || ''} ${user.lastname || ''}`.trim();
    }
    return user.username || null;
  };

  const getDesignerSchool = () => {
    return product.users_permissions_user?.school || '';
  };

  const designerName = getDesignerName();
  const designerSchool = getDesignerSchool();

  return (
    <div
      className="group relative rounded-lg overflow-hidden cursor-pointer transition-all duration-500 flex flex-col"
      style={{
        background: '#FFFFFF',
        transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
        boxShadow: isHovered ? '0 12px 32px rgba(26,26,26,0.08)' : '0 1px 2px rgba(26,26,26,0.04)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => handleCardClick(product)}
    >
      {/* Image — clean, no badges */}
      <div className="relative overflow-hidden flex-1 flex flex-col w-full" style={hasPricing ? { aspectRatio: '3/4' } : {}}>
        <div className="flex-1 w-full h-full min-h-0 relative" style={{ aspectRatio: '3/4' }}>
        {product.photos && product.photos.length > 0 ? (
          <img
            src={product.generatedProductPhoto?.url || product.photos[product.coverImageIndex]?.url || product.photos[0]?.url}
            alt={product.name || 'Design'}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-600"
            style={{
              transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
              transform: isHovered ? 'scale(1.03)' : 'scale(1)',
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 bg-gray-50">
            <span className="text-sm">No Image</span>
          </div>
        )}
        </div>

        {/* Like button — top right */}
        <div
          className={`absolute top-2.5 right-2.5 z-20 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
          onClick={handleInteractiveClick}
        >
          <div className="bg-white rounded-lg border border-gray-200 px-1 hover:bg-gray-50 transition-colors flex items-center">
            <Like productId={product.id} size="sm" showCount={false} />
          </div>
        </div>

        {/* Add to Closet — top left */}
        <div
          className={`absolute top-2.5 left-2.5 z-30 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
          onClick={handleInteractiveClick}
        >
          <div className="shadow-sm">
            <AddToClosetButton listingId={product.id} className="text-xs" />
          </div>
        </div>

        {/* Type label — visible by default, hides on hover */}
        {(product.isAiGenerated || product.is3dModel) && (
          <div
            className="absolute bottom-2 left-2.5 z-10 transition-all duration-300 opacity-80 group-hover:opacity-0 pointer-events-none"
          >
            <span
              className="inline-flex items-center gap-1 text-white px-1.5 py-0.5 rounded"
              style={{ fontSize: 10, fontWeight: 500, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' }}
            >
              {product.isAiGenerated && (
                <>
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1L9.5 6.5L15 8L9.5 9.5L8 15L6.5 9.5L1 8L6.5 6.5L8 1Z" fill="currentColor"/>
                  </svg>
                  AI designed
                </>
              )}
              {product.is3dModel && !product.isAiGenerated && '3D model'}
            </span>
          </div>
        )}

        {/* Designer name overlay — inside image, shown on hover */}
        {designerName && (
          <div
            className="absolute bottom-0 left-0 right-0 p-4 z-10 transition-all duration-400 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(26,26,26,0.75) 0%, rgba(26,26,26,0.15) 70%, transparent 100%)',
            }}
          >
            <p className="text-white text-sm font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
              {designerName}{designerSchool ? ` · ${designerSchool}` : ''}
            </p>
          </div>
        )}
      </div>

      {/* Footer — pricing, pushed to bottom */}
      {hasPricing && (
        <div className="px-3 py-3 mt-auto bg-white">
          <PriceBar product={product} />
        </div>
      )}
    </div>
  );
};

export default BrowseCard;